import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { useDateLocale } from '../../../utils/dateLocale';
import { dayDropId } from './dayDropTarget';
import type { ItineraryDay } from '../../../types';

const dayDate = (day: ItineraryDay) => parseISO(day.date.slice(0, 10));

/**
 * The other days of the trip, as drop targets, shown only while a stop is being
 * dragged.
 *
 * It appears with the drag rather than sitting permanently above it for two
 * reasons: the targets are meaningless when nothing is moving, and on a phone
 * the day strip is often scrolled out of view by the time the finger has picked
 * a card up — a target you cannot see is not a target.
 *
 * Fixed to the viewport rather than inserted above the rail: appearing inside
 * the list would push every card down at the exact moment dnd-kit has finished
 * measuring them, and the drop targets would then sit a bar's height away from
 * where they are drawn.
 *
 * The heading names the armed day: the dragged card covers the chip itself, so
 * the chip alone cannot tell the user where the stop is about to go.
 */
export function DayDropBar({ days, currentDayId, hoverDayId, armedDayId }: {
  days: ItineraryDay[];
  currentDayId: string;
  hoverDayId: string | null;
  armedDayId: string | null;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const others = days.filter(d => d.id !== currentDayId);
  if (others.length === 0) return null;
  const armed = others.find(d => d.id === armedDayId);

  return (
    <div className={`fixed bottom-36 sm:bottom-24 left-1/2 -translate-x-1/2 z-50 max-w-[92vw] rounded-2xl bg-white/95 dark:bg-slate-900/95 backdrop-blur border shadow-2xl px-4 py-3 animate-fade-in transition-colors ${
      armed ? 'border-teal-400 dark:border-teal-600' : 'border-slate-200 dark:border-slate-700'
    }`}>
      <p className={`text-xs font-semibold mb-2 text-center ${armed ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400'}`}>
        {armed
          ? t('itinerary.move.releaseOnDay', {
            day: armed.dayNumber,
            date: format(dayDate(armed), 'd MMM', { locale: dateLocale }),
          })
          : t('itinerary.move.dropOnDay')}
      </p>
      <div className="flex gap-2 overflow-x-auto scrollbar-none">
        {others.map(day => (
          <DayDropChip
            key={day.id}
            day={day}
            state={day.id === armedDayId ? 'armed' : day.id === hoverDayId ? 'arming' : 'idle'}
          />
        ))}
      </div>
    </div>
  );
}

const CHIP_STYLE = {
  idle:   'border-dashed border-slate-300 dark:border-slate-600 bg-slate-50/60 dark:bg-slate-800/40',
  arming: 'border-dashed border-teal-300 dark:border-teal-700 bg-teal-50/40 dark:bg-teal-900/10',
  armed:  'border-solid border-teal-500 bg-teal-50 dark:bg-teal-900/30 scale-105',
} as const;

function DayDropChip({ day, state }: { day: ItineraryDay; state: keyof typeof CHIP_STYLE }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { setNodeRef } = useDroppable({ id: dayDropId(day.id) });

  return (
    <div
      ref={setNodeRef}
      data-state={state}
      className={`shrink-0 min-w-[88px] px-3 py-2.5 rounded-xl border-2 text-center transition-all ${CHIP_STYLE[state]}`}
    >
      <span className={`block text-xs font-semibold leading-none ${
        state === 'armed' ? 'text-teal-600 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'
      }`}>
        {t('itinerary.dayShort', { n: day.dayNumber })}
      </span>
      <span className="block mt-1 text-2xs leading-none text-slate-400 whitespace-nowrap">
        {format(dayDate(day), 'd MMM', { locale: dateLocale })}
      </span>
    </div>
  );
}
